import React, { ChangeEvent, FC, useState } from 'react';
import { Order } from 'models/order';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { certifyUnCertifyOrderTemplate } from 'apollo/templates/order';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import PriceSpecificationSectionWithoutHighlighting from 'components/order-details/main-order-view/price-specification-section/price-specification-section-without-highlighting.componet';

type CertifyUncertifyOrderDialogProps = {
    onUpdate?: (order: Order) => void;
    onClose: () => void;
    open: boolean;
    order: Order;
    currency: string;
};

const CertifyUncertifyOrderDialog: FC<CertifyUncertifyOrderDialogProps> = ({
    onUpdate,
    onClose,
    open,
    order,
    currency,
}: CertifyUncertifyOrderDialogProps) => {
    const { t: translate } = useTranslation(['orders']);
    const [isPriceConfirmed, setIsPriceConfirmed] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();

    const [changeStatus] = useMutation(certifyUnCertifyOrderTemplate, {
        onCompleted: ({ certifyUnCertifyOrder }) => {
            onClose();
            onUpdate?.({ ...order, ...certifyUnCertifyOrder });
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleChangeStatus = async () => {
        await changeStatus({
            variables: {
                orderId: order?._id,
            },
        });
    };

    const handleConfirmPrice = (event: ChangeEvent<HTMLInputElement>) => {
        setIsPriceConfirmed(event.target.checked);
    };

    return (
        <div>
            <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
                <DialogTitle>{translate('orders:CERTIFY_ORDER')}</DialogTitle>
                <DialogContent>
                    <Box mb={3}>
                        <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                            {translate('orders:APPROVE_WARNING_MESSAGE')}
                        </Alert>
                    </Box>
                    <Box mb={3}>
                        <PriceSpecificationSectionWithoutHighlighting order={order} currency={currency} />
                        <FormControl margin="dense" fullWidth>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="secondary"
                                        checked={isPriceConfirmed}
                                        onChange={handleConfirmPrice}
                                    />
                                }
                                label={translate('orders:CONFIRM_CERTIFY')}
                            />
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>{translate('orders:NO')}</Button>

                    <Button disabled={!isPriceConfirmed} variant="contained" onClick={handleChangeStatus}>
                        {translate('orders:YES')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};
export default CertifyUncertifyOrderDialog;
